import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  Box,
  MenuItem,
  InputAdornment,
  FormHelperText,
  Alert,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  AttachMoney as MoneyIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { State, City, Country } from "country-state-city";
import axios from "axios";


// Constants
const ADMIN_RATE = 50;
const STANDARD_USER_RATE = 5;
const MIN_LLM_LIMIT = 10;
const PROJECT_RATE =25;

// Custom hooks
const usePreventScroll = () => {
  useEffect(() => {
    const preventScroll = (e) => {
      const target = e.target;
      if (
        target.classList.contains("MuiSelect-select") ||
        target.closest(".MuiMenu-paper")
      ) {
        e.stopPropagation();
      }
    };

    document.addEventListener("wheel", preventScroll, { passive: false });
    return () => document.removeEventListener("wheel", preventScroll);
  }, []);
};

// Form validation utility
const validateForm = (formData) => {
  const errors = {};

  // Basic validations
  if (!formData.organization_name?.trim()) {
    errors.organization_name = "Organization name is required";
  }

  if (!formData.admin_name?.trim()) {
    errors.admin_name = "Admin name is required";
  }

  if (!formData.admin_email?.trim()) {
    errors.admin_email = "Admin email is required";
  } else if (!/\S+@\S+\.\S+/.test(formData.admin_email)) {
    errors.admin_email = "Please enter a valid email address";
  }

  // Address validations
  if (!formData.organization_address?.trim()) {
    errors.organization_address = "Address is required";
  }

  if (!formData.country) {
    errors.country = "Country is required";
  }

  if (!formData.state) {
    errors.state = "State is required";
  }

  if (!formData.city) {
    errors.city = "City is required";
  }

  // Contact validation
  if (!formData.contact_number) {
    errors.contact_number = "Contact number is required";
  } else if (formData.contact_number.length !== 10) {
    errors.contact_number = "Contact number must be exactly 10 digits";
  }

  // Cost & user validations
  if (!formData.initial_cost_limit) {
    errors.initial_cost_limit = "Initial cost is required";
  } else if (
    isNaN(formData.initial_cost_limit) ||
    Number(formData.initial_cost_limit) < MIN_LLM_LIMIT
  ) {
    errors.initial_cost_limit = `Minimum LLM Usage Limit is $${MIN_LLM_LIMIT}.00`;
  }
  if (!formData.number_of_projects) {
    errors.number_of_projects = "Number of projects is required";
  } else if (!/^[1-9]\d*$/.test(formData.number_of_projects)) {
    errors.number_of_projects = "Must be a positive integer";
  }
  if (!formData.number_of_admin) {
    errors.number_of_admin = "Number of admin is required";
  } else if (!/^[1-9]\d*$/.test(formData.number_of_admin)) {
    errors.number_of_admin = "Must be a positive integer";
  }

  if (!formData.number_of_user) {
    errors.number_of_user = "Number of user is required";
  } else if (!/^[1-9]\d*$/.test(formData.number_of_user)) {
    errors.number_of_user = "Must be a positive integer";
  }

  return errors;
};

// Sub-components
const FormSection = ({ title, icon: Icon, children }) => (
  <Card variant="outlined">
    <CardContent>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", mb: 3 }}
      >
        <Icon sx={{ mr: 1, color: "primary.main" }} />
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);


const CostBreakdownTable = ({
  adminCount,
  userCount,
  projectCount,
  llmLimit,
}) => {
  // Calculate paid/free units
  const freeAdmin = adminCount > 0 ? 1 : 0;
  const paidAdmin = Math.max(0, adminCount - freeAdmin);

  const freeUser = userCount > 0 ? 1 : 0;
  const paidUser = Math.max(0, userCount - freeUser);

  const freeProject = projectCount > 0 ? 1 : 0;
  const paidProject = Math.max(0, projectCount - freeProject);

  const adminTotal = paidAdmin * ADMIN_RATE;
  const userTotal = paidUser * STANDARD_USER_RATE;
  const projectTotal = paidProject * PROJECT_RATE;
  const llmTotal = parseFloat(llmLimit) || 0;
  const total = adminTotal + userTotal + projectTotal ;

  return (
    <Table sx={{ minWidth: 400, backgroundColor: "white" }}>
      <TableHead>
        <TableRow sx={{ backgroundColor: "#42a5f5" }}>
          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Item</TableCell>
          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Total Units</TableCell>
          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Free Units</TableCell>
          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Paid Units</TableCell>
          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Rate</TableCell>
          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Amount</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell>Admin Users</TableCell>
          <TableCell>{adminCount}</TableCell>
          <TableCell>{freeAdmin}</TableCell>
          <TableCell>{paidAdmin}</TableCell>
          <TableCell>${ADMIN_RATE}.00</TableCell>
          <TableCell>
            {paidAdmin === 0 ? (
              <span style={{ color: "#43a047", fontWeight: "bold" }}>Free</span>
            ) : (
              `$${adminTotal.toFixed(2)}`
            )}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Standard Users</TableCell>
          <TableCell>{userCount}</TableCell>
          <TableCell>{freeUser}</TableCell>
          <TableCell>{paidUser}</TableCell>
          <TableCell>${STANDARD_USER_RATE}.00</TableCell>
          <TableCell>
            {paidUser === 0 ? (
              <span style={{ color: "#43a047", fontWeight: "bold" }}>Free</span>
            ) : (
              `$${userTotal.toFixed(2)}`
            )}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>Projects</TableCell>
          <TableCell>{projectCount}</TableCell>
          <TableCell>{freeProject}</TableCell>
          <TableCell>{paidProject}</TableCell>
          <TableCell>${PROJECT_RATE}.00</TableCell>
          <TableCell>
            {paidProject === 0 ? (
              <span style={{ color: "#43a047", fontWeight: "bold" }}>Free</span>
            ) : (
              `$${projectTotal.toFixed(2)}`
            )}
          </TableCell>
        </TableRow>
        {/* <TableRow>
          <TableCell>LLM Usage Limit</TableCell>
          <TableCell colSpan={3}>-</TableCell>
          <TableCell>-</TableCell>
          <TableCell>${llmTotal.toFixed(2)}</TableCell>
        </TableRow> */}
        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
          <TableCell colSpan={5} sx={{ fontWeight: "bold" }}>
            Total Monthly Cost
          </TableCell>
          <TableCell sx={{ fontWeight: "bold" }}>${total.toFixed(2)}</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

const RegisterOrganization = () => {
  const navigate = useNavigate();
  usePreventScroll();

  // Form state
  const [formData, setFormData] = useState({
    organization_name: "",
    admin_name: "",
    organization_address: "",
    country: "",
    state: "",
    city: "",
    country_code: "",
    contact_number: "",
    admin_email: "",
    initial_cost_limit: "",
    number_of_admin: "",
    number_of_user: "",
    number_of_projects: "",
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});

  // Location data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // Load countries on mount
  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

  // Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleCountryChange = (e) => {
    const selectedCountryCode = e.target.value;
    const selectedCountry = countries.find(
      (country) => country.isoCode === selectedCountryCode
    );

    setFormData((prev) => ({
      ...prev,
      country: selectedCountryCode,
      state: "",
      city: "",
      country_code: selectedCountry ? selectedCountry.phonecode : "",
    }));

    if (selectedCountryCode) {
      setStates(State.getStatesOfCountry(selectedCountryCode) || []);
      setCities([]);
    } else {
      setStates([]);
      setCities([]);
    }

    if (errors.country) {
      setErrors((prev) => ({ ...prev, country: "" }));
    }
  };

  const handleStateChange = (e) => {
    const selectedStateCode = e.target.value;
    setFormData((prev) => ({
      ...prev,
      state: selectedStateCode,
      city: "",
    }));

    if (selectedStateCode && formData.country) {
      setCities(City.getCitiesOfState(formData.country, selectedStateCode));
    } else {
      setCities([]);
    }

    if (errors.state) {
      setErrors((prev) => ({ ...prev, state: "" }));
    }
  };

  const handleContactNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) {
      setFormData((prev) => ({
        ...prev,
        contact_number: value,
      }));
    }
  };

  const handleCostLimitChange = (e) => {
    const value = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        initial_cost_limit: value,
      }));
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setErrorMessage("Please fill in all required fields correctly.");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    https: try {
      const response = await axios.post(
        // "https://app-llminsight-saas-uat-api.azurewebsites.net/api/saas_request_organization",
        "https://saas-dev-api.llminsight.ai/api/saas_request_organization",
        // "https://saas-api.llminsight.ai/api/saas_request_organization",
        // "http://127.0.0.1:5000/api/saas_request_organization",
        {
          ...formData,
          initial_cost_limit: parseFloat(formData.initial_cost_limit),
          numberofadmin: formData.number_of_admin,
          numberofuser: formData.number_of_user,
          numberofprojects: formData.number_of_projects,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 201) {
        setSuccessMessage(
          "Organization registration request submitted successfully! Please check your email for further instructions."
        );
        setFormData({
          organization_name: "",
          admin_name: "",
          admin_email: "",
          organization_address: "",
          country: "",
          state: "",
          city: "",
          country_code: "",
          contact_number: "",
          initial_cost_limit: "",
          number_of_admin: "",
          number_of_user: "",
        });
        setTimeout(() => {
          navigate("/");
        }, 2000);
      }
    } catch (error) {
      console.error("Registration error:", error);
      setErrorMessage(
        error.response?.data?.error ||
          "Failed to register organization. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 12, mb: 4 }}>
      <Paper elevation={3} sx={{ overflow: "hidden" }}>
        {successMessage && (
          <Alert severity="success" sx={{ m: 3 }}>
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ m: 3 }}>
            {errorMessage}
          </Alert>
        )}

        <Box>
          {/* sx={{ p: 4 }}> */}
          <Grid container spacing={4}>
            {/* Organization Details */}
            <Grid item xs={12}>
              <FormSection title="Organization Details" icon={BusinessIcon}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Organization Name"
                      name="organization_name"
                      value={formData.organization_name}
                      onChange={handleInputChange}
                      required
                      error={!!errors.organization_name}
                      helperText={errors.organization_name}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Admin Name"
                      name="admin_name"
                      value={formData.admin_name}
                      onChange={handleInputChange}
                      required
                      error={!!errors.admin_name}
                      helperText={errors.admin_name}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Admin Email"
                      name="admin_email"
                      type="email"
                      value={formData.admin_email}
                      onChange={handleInputChange}
                      required
                      error={!!errors.admin_email}
                      helperText={errors.admin_email}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </FormSection>
            </Grid>

            {/* Address Information */}
            <Grid item xs={12}>
              <FormSection title="Address Information" icon={LocationIcon}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      name="organization_address"
                      value={formData.organization_address}
                      onChange={handleInputChange}
                      required
                      multiline
                      rows={2}
                      error={!!errors.organization_address}
                      helperText={errors.organization_address}
                      placeholder="Enter complete address"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      select
                      label="Country"
                      name="country"
                      value={formData.country}
                      onChange={handleCountryChange}
                      required
                      error={!!errors.country}
                      helperText={errors.country}
                    >
                      <MenuItem value="">Select Country</MenuItem>
                      {countries.map((country) => (
                        <MenuItem key={country.isoCode} value={country.isoCode}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      select
                      label="State"
                      name="state"
                      value={formData.state}
                      onChange={handleStateChange}
                      required
                      disabled={!formData.country}
                      error={!!errors.state}
                      helperText={errors.state}
                    >
                      <MenuItem value="">Select State</MenuItem>
                      {states.map((state) => (
                        <MenuItem key={state.isoCode} value={state.isoCode}>
                          {state.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      select
                      label="City"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      disabled={!formData.state}
                      error={!!errors.city}
                      helperText={errors.city}
                    >
                      <MenuItem value="">Select City</MenuItem>
                      {cities.map((city) => (
                        <MenuItem key={city.name} value={city.name}>
                          {city.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </FormSection>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <FormSection title="Contact Information" icon={PhoneIcon}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Mobile Number"
                      name="contact_number"
                      value={formData.contact_number}
                      onChange={handleContactNumberChange}
                      required
                      error={!!errors.contact_number}
                      helperText={
                        errors.contact_number ||
                        `${formData.contact_number.length}/10 digits`
                      }
                      placeholder="Enter Your Mobile Number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <span
                              style={{
                                fontWeight: 500,
                                fontSize: 15,
                                marginRight: 6,
                              }}
                            >
                              {formData.country_code
                                ? `+${formData.country_code}`
                                : ""}
                            </span>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </FormSection>
            </Grid>

            {/* Cost & User Details */}
            <Grid item xs={12}>
              <FormSection title="Cost & User Details" icon={MoneyIcon}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <TextField
                        fullWidth
                        label="LLM Usage Limit"
                        name="initial_cost_limit"
                        value={formData.initial_cost_limit}
                        onChange={handleCostLimitChange}
                        required
                        error={!!errors.initial_cost_limit}
                        placeholder="Enter amount (e.g., 1000.00)"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MoneyIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Tooltip
                        title="Set your monthly LLM usage limit. Minimum is $10.00. You can increase this limit anytime."
                        placement="top"
                        arrow
                      >
                        <InfoOutlinedIcon
                          sx={{
                            ml: 1,
                            color: "primary.main",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </Box>
                    {/* <FormHelperText sx={{ mt: 1 }}>
                      Note: The first $5 is pre-credited for LLM usage. This
                      amount is your monthly limit for LLM features. If you
                      exhaust these credits, you’ll need to add more to
                      continue.
                    </FormHelperText> */}
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Number of Projects"
                      name="number_of_projects"
                      value={formData.number_of_projects}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          number_of_projects: e.target.value.replace(/\D/g, ""),
                        }))
                      }
                      required
                      error={!!errors.number_of_projects}
                      helperText={errors.number_of_projects}
                      placeholder="Enter number of projects"
                      type="number"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Number of Admin"
                      name="number_of_admin"
                      value={formData.number_of_admin}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          number_of_admin: e.target.value.replace(/\D/g, ""),
                        }))
                      }
                      required
                      error={!!errors.number_of_admin}
                      helperText={errors.number_of_admin}
                      placeholder="Enter number of admins"
                      type="number"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Number of User"
                      name="number_of_user"
                      value={formData.number_of_user}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          number_of_user: e.target.value.replace(/\D/g, ""),
                        }))
                      }
                      required
                      error={!!errors.number_of_user}
                      helperText={errors.number_of_user}
                      placeholder="Enter number of users"
                      type="number"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                </Grid>

                {formData.number_of_admin && formData.number_of_user && (
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, fontWeight: "medium" }}
                    >
                      Cost Breakdown
                    </Typography>
                    <CostBreakdownTable
                      adminCount={Number(formData.number_of_admin)}
                      userCount={Number(formData.number_of_user)}
                      projectCount={Number(formData.number_of_projects)}
                      llmLimit={formData.initial_cost_limit}
                    />
                  </Box>
                )}
              </FormSection>
            </Grid>

            {/* Pricing Information */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ bgcolor: "#f8f9fa" }}>
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: "text.secondary",
                      fontStyle: "italic",
                      mb: 1,
                      fontWeight: "bold",
                    }}
                  >
                    Pricing Information
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5, fontStyle: "italic" }}
                  >
                    • LLM access includes a one-time free $5 credit upon
                    registration. If your usage exceeds this limit, additional
                    credits must be purchased.
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5, fontStyle: "italic" }}
                  >
                    • 1 Admin, 1 User, and 1 Project are provided free for the
                    lifetime of your account.
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    The initial cost pertains to the use of Large Language
                    Models (LLMs) and will be due at the end of the month. The
                    pricing is structured as follows:
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      • Admin users: ${ADMIN_RATE} per user
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      • Project: ${PROJECT_RATE} per project
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      • Standard users: ${STANDARD_USER_RATE} per user
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5, fontStyle: "italic" }}
                    >
                      • LLM access requires a minimum prepaid credit of $5.00.
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5, fontStyle: "italic" }}
                    >
                      • Admin and Standard User fees are waived for the first
                      month of service.
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: "italic" }}
                    >
                      All charges, including those for Admin, User will be
                      billed at the end of each month.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "center",
              alignItems: "center",
              gap: { xs: 2, sm: 3 },
              mt: 4,
              pt: 3,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate("/")}
              disabled={loading}
              startIcon={<CancelIcon />}
              sx={{
                minWidth: { xs: "100%", sm: "140px" },
                order: { xs: 2, sm: 1 },
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={20} /> : <SaveIcon />
              }
              sx={{
                minWidth: { xs: "100%", sm: "200px" },
                order: { xs: 1, sm: 2 },
                background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
              }}
            >
              {loading ? "Registering..." : "Register Organization"}
            </Button>
          </Box>

          {/* Additional Info */}
          <Box sx={{ mt: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              🔒 Your information is secure and encrypted. After registration,
              admin credentials will be sent to your email.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default RegisterOrganization;
